// CountdownTimer — tempo restante até um instante (MET-25: janela de 24h do
// WhatsApp; MET-04: prazo de SLA).
//
// Duas decisões que carregam a correção:
//
// 1. **Sem `aria-live` no contador.** É a armadilha do requisito: N contadores
//    numa lista, todos em `polite`, inundam o leitor de tela com uma leitura por
//    segundo e a página fica inutilizável. O resumo agregado ("3 atendimentos com
//    janela expirando") é que vai num único `aria-live`, ACIMA da lista, e é
//    responsabilidade da view — não deste componente.
// 2. **Um `setInterval` para todos**, via ticker compartilhado (`./ticker`).
//
// `<time dateTime>` porque o valor é um instante de máquina: dá ao leitor de tela
// e a qualquer parser o prazo absoluto, enquanto o texto mostra o relativo.
import { useMemo } from 'react';
import { cn } from '@/lib/utils/cn';
import { formatDuration } from '@/lib/utils/duration';
import { useCountdownTick } from './CountdownTickerProvider';
import styles from './CountdownTimer.module.css';

export type CountdownTone = 'neutral' | 'warning' | 'danger';

export interface CountdownTimerProps {
  /** Instante-alvo em ISO 8601. `null` = sem prazo conhecido ⇒ mostra `—`. */
  to: string | null | undefined;
  /** Texto quando o prazo já passou. Default "Expirado". */
  expiredLabel?: string;
  /** Abaixo deste tempo restante a pill vira `warning`. Default 2h. */
  warnBelowMs?: number;
  /** Abaixo deste tempo restante a pill vira `danger`. Default 30min. */
  dangerBelowMs?: number;
  /** `clock` (01:12:30) ou `short` (1h 12m). Default `short`. */
  style?: 'clock' | 'short';
  /** Prefixo textual acessível ("Janela fecha em"). Entra no title, não na tela. */
  srPrefix?: string;
  className?: string;
}

const TWO_HOURS = 2 * 60 * 60 * 1000;
const THIRTY_MIN = 30 * 60 * 1000;

const toneClass: Record<CountdownTone, string> = {
  neutral: styles.neutral,
  warning: styles.warning,
  danger: styles.danger,
};

export function CountdownTimer({
  to,
  expiredLabel = 'Expirado',
  warnBelowMs = TWO_HOURS,
  dangerBelowMs = THIRTY_MIN,
  style = 'short',
  srPrefix,
  className,
}: CountdownTimerProps) {
  const now = useCountdownTick();

  const target = useMemo(() => {
    if (!to) return null;
    const parsed = Date.parse(to);
    return Number.isNaN(parsed) ? null : parsed;
  }, [to]);

  // Sem prazo conhecido é "—", nunca "00:00" (que se leria como "expira agora").
  if (target === null) {
    return <span className={cn(styles.timer, styles.neutral, className)}>—</span>;
  }

  const remaining = target - now;
  const expired = remaining <= 0;
  const tone: CountdownTone = expired
    ? 'danger'
    : remaining <= dangerBelowMs
      ? 'danger'
      : remaining <= warnBelowMs
        ? 'warning'
        : 'neutral';

  const text = expired ? expiredLabel : formatDuration(remaining, { style });

  return (
    <time
      dateTime={new Date(target).toISOString()}
      className={cn(styles.timer, toneClass[tone], className)}
      data-expired={expired || undefined}
      title={srPrefix ? `${srPrefix} ${text}` : undefined}
    >
      {text}
    </time>
  );
}
